import React, { useState } from 'react'
import { Download, Mail, CreditCard, Users, Database, Shield, BarChart3 } from 'lucide-react'
import Seo from '../../Components/SeoComponent/Seo';
import Header from '../../Components/Header';

const BillingInvoice = () => {
  const [invoiceData] = useState({
    invoiceNumber: 'INV-2025-001247',
    issueDate: '2025-08-15',
    dueDate: '2025-09-15',
    status: 'Paid',
    company: {
      name: 'FinTech Solutions Ltd.',
      address: '123 Banking Street, Financial District',
      city: 'New York, NY 10005',
      email: 'billing@fintech-solutions.com',
      phone: '+1 (555) 123-4567'
    },
    client: {
      name: 'Digital Banking Corp',
      address: '456 Innovation Ave',
      city: 'San Francisco, CA 94105',
      email: 'accounts@digitalbanking.com',
      taxId: 'TAX-123456789'
    },
    services: [
      {
        id: 1,
        icon: <CreditCard className="w-5 h-5" />,
        description: 'Payment Processing API',
        details: 'Transaction processing (50,000 calls)',
        quantity: 50000,
        rate: 0.025,
        amount: 1250.00
      },
      {
        id: 2,
        icon: <Shield className="w-5 h-5" />,
        description: 'KYC Verification API',
        details: 'Identity verification (2,500 verifications)',
        quantity: 2500,
        rate: 0.75,
        amount: 1875.00
      },
      {
        id: 3,
        icon: <Users className="w-5 h-5" />,
        description: 'Account Management Services',
        details: 'Premium support & maintenance',
        quantity: 1,
        rate: 899.00,
        amount: 899.00
      },
      {
        id: 4,
        icon: <Database className="w-5 h-5" />,
        description: 'Data Analytics Dashboard',
        details: 'Real-time reporting & insights',
        quantity: 1,
        rate: 499.00,
        amount: 499.00
      },
      {
        id: 5,
        icon: <BarChart3 className="w-5 h-5" />,
        description: 'Risk Assessment API',
        details: 'Credit scoring (1,200 assessments)',
        quantity: 1200,
        rate: 1.50,
        amount: 1800.00
      }
    ]
  })

  const subtotal = invoiceData.services.reduce((sum, service) => sum + service.amount, 0)
  const taxRate = 0.08
  const taxAmount = subtotal * taxRate
  const total = subtotal + taxAmount

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200'
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  return (

 <>
     <Seo />
         <Header heading='Profile' />
    <div className="min-h-screen bg-gray-50 py-1 px-1">
      <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2">INVOICE</h1>
              <div className="space-y-1 text-blue-100">
                <p className="text-lg font-semibold">{invoiceData.company.name}</p>
                <p>{invoiceData.company.address}</p>
                <p>{invoiceData.company.city}</p>
                <p>{invoiceData.company.email}</p>
                <p>{invoiceData.company.phone}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-white bg-opacity-20 rounded-lg p-2 mb-2">
                <p className="text-sm text-blue-100">Invoice Number</p>
                <p className="text-xl font-bold">{invoiceData.invoiceNumber}</p>
              </div>
              <div className={`inline-block px-3 py-2 rounded-full border ${getStatusColor(invoiceData.status)}`}>
                <span className="font-semibold">{invoiceData.status}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="p-4">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill To:</h3>
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="font-semibold text-gray-900">{invoiceData.client.name}</p>
                <p className="text-gray-600">{invoiceData.client.address}</p>
                <p className="text-gray-600">{invoiceData.client.city}</p>
                <p className="text-gray-600">{invoiceData.client.email}</p>
                <p className="text-gray-600">Tax ID: {invoiceData.client.taxId}</p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Invoice Details:</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Issue Date:</span>
                  <span className="font-medium">{invoiceData.issueDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium">{invoiceData.dueDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Terms:</span>
                  <span className="font-medium">Net 30</span>
                </div>
              </div>
            </div>
          </div>

          {/* Services Table */}
          <div className="overflow-x-auto mb-4">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-700">Service</th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700">Quantity</th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700">Rate</th>
                  <th className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.services.map((service) => (
                  <tr key={service.id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-2 py-2">
                      <div className="flex items-start space-x-3">
                        <div className="text-blue-600 mt-1">{service.icon}</div>
                        <div>
                          <p className="font-semibold text-gray-900">{service.description}</p>
                          <p className="text-sm text-gray-600">{service.details}</p>
                        </div>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-4 text-right font-medium">
                      {service.quantity.toLocaleString()}
                    </td>
                    <td className="border border-gray-300 px-4 py-4 text-right font-medium">
                      ${service.rate.toFixed(2)}
                    </td>
                    <td className="border border-gray-300 px-4 py-4 text-right font-semibold">
                      ${service.amount.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-full max-w-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax (8%):</span>
                    <span className="font-medium">${taxAmount.toFixed(2)}</span>
                  </div>
                  <hr className="border-gray-300" />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-blue-600">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-800 mb-3">Payment Information</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-blue-700 font-medium">Bank Details:</p>
                <p>Account Name: FinTech Solutions Ltd.</p>
                <p>Account Number: 1234567890</p>
                <p>Routing Number: 987654321</p>
                <p>SWIFT Code: FTSUS33XXX</p>
              </div>
              <div>
                <p className="text-blue-700 font-medium">Online Payment:</p>
                <p>Portal: billing.fintech-solutions.com</p>
                <p>Reference: {invoiceData.invoiceNumber}</p>
                <p>Cards accepted: Visa, MasterCard, Amex</p>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-end">
            <button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors">
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
            <button className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors">
              <Mail className="w-4 h-4" />
              <span>Send Email</span>
            </button>
          </div>

        </div>
      </div>
    </div>
    </>
  )
}

export default BillingInvoice